import React, { useContext, useEffect, useState, useRef } from 'react'
import { Icon } from '@leme/shared-components'
import { useQueryClient } from '@tanstack/react-query'
import { BooksContext } from '../../contexts/BooksContext'
import services from '../../services'
import classes from './FavoriteButton.module.css'
import { TRACKING_EVENTS } from '../../constants/TrackingEvents'

export default function FavoriteButton({
  book,
  runAfterOnClick,
  isFavorite,
  variant = 'lg'
}) {
  const { unfavoriteBook, favoriteBook, invalidateFavoritesCache } =
    useContext(BooksContext)
  const [bookIsFavorite, setBookIsFavorite] = useState(isFavorite)
  const [isLoading, setIsLoading] = useState(false)

  const queryClient = useQueryClient()
  const FAVORITES_QUERY_KEY = 'favorites'
  const PARSED_FAVORITES_KEY = 'parsed-favorites'

  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (isMounted.current) {
      setBookIsFavorite(isFavorite)
    }
  }, [isFavorite])

  const invalidateAllFavoritesCaches = () => {
    queryClient.invalidateQueries([FAVORITES_QUERY_KEY])

    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === PARSED_FAVORITES_KEY
    })

    if (invalidateFavoritesCache) {
      invalidateFavoritesCache()
    }

    queryClient.removeQueries([FAVORITES_QUERY_KEY])
    queryClient.removeQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === PARSED_FAVORITES_KEY
    })
  }

  const onClick = async () => {
    if (isLoading) return

    const currentlyFavorite = bookIsFavorite

    if (isMounted.current) {
      setIsLoading(true)
      setBookIsFavorite(!currentlyFavorite)

      if (runAfterOnClick) {
        runAfterOnClick(!currentlyFavorite, false)
      }
    }

    let operationSuccessful = false

    try {
      const favoriteBooks = JSON.parse(localStorage.getItem('favorites')) || []

      if (currentlyFavorite) {
        const favoriteItem = favoriteBooks.find(
          (item) => item.favoriteId === book.uuid
        )

        if (favoriteItem) {
          await unfavoriteBook(favoriteItem.id)

          invalidateAllFavoritesCaches()

          await services.Trackings.favoritesTracking(book, {
            event: TRACKING_EVENTS.UNFAVORITE_BOOK
          })

          operationSuccessful = true
        } else {
          console.warn(`Favorito não encontrado para remoção: ${book.uuid}`)

          try {
            const freshFavorites = await queryClient.fetchQuery([
              FAVORITES_QUERY_KEY
            ])

            if (freshFavorites && freshFavorites.favoriteObjectArray) {
              const item = freshFavorites.favoriteObjectArray.find(
                (f) => f.favoriteId === book.uuid
              )

              if (item) {
                await unfavoriteBook(item.id)
                operationSuccessful = true

                invalidateAllFavoritesCaches()
              }
            }
          } catch (fetchError) {
            console.error('Erro ao buscar favoritos atualizados:', fetchError)
          }
        }
      } else {
        const response = await favoriteBook(book.uuid)

        if (response && response.id) {
          favoriteBooks.push({
            favoriteId: book.uuid,
            id: response.id
          })

          invalidateAllFavoritesCaches()

          await services.Trackings.favoritesTracking(book, {
            event: TRACKING_EVENTS.FAVORITE_BOOK
          })

          operationSuccessful = true
        }
      }

      if (operationSuccessful && runAfterOnClick && isMounted.current) {
        runAfterOnClick(!currentlyFavorite, true)
      }
    } catch (error) {
      console.error('Erro ao processar favorito:', error)

      if (isMounted.current) {
        setBookIsFavorite(currentlyFavorite)

        if (runAfterOnClick) {
          runAfterOnClick(currentlyFavorite, false)
        }
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false)
      }
    }
  }

  return (
    <button
      className={classes.button}
      onClick={onClick}
      data-testid="favorite-button"
      data-variant={variant}
      disabled={isLoading}
    >
      {bookIsFavorite ? (
        <Icon name={'favorite'} customCss={classes.heartIconFilled} />
      ) : (
        <Icon name={'favorite'} customCss={classes.heartIcon} />
      )}
    </button>
  )
}
